// useCartLogic.ts

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchBag, finalizeOrder, proceedOrder, cancelOrder } from "../api/apiCartPage";
import { openRazorpayCheckout } from "../utilities/razorpayClient";
import { PAYMENT_MODE } from "../utils/cartUtils";
import { CartProduct } from "../types/cart.types";
import {
  getCartItems,
  getUserType,
  removeCartItem,
  resolveUid,
  updateCartItem,
} from "../utilities/cookieUtils";
import { getEncodedTimestamp } from "../utilities/dateUtils";

export const useCartLogic = () => {
  const [cartItems, setCartItems] = useState<CartProduct[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingQtyUpdates, setPendingQtyUpdates] = useState<Record<string, number>>({});
  // Track available payment modes returned by the Bag API
  const [enableMode, setEnableMode] = useState<{ cash: boolean; online: boolean }>({ cash: true, online: true });

  // ✅ Guard ref to prevent duplicate fetches on reload for logged-in users
  const hasFetchedRef = useRef(false);
  const userTypeRef = useRef<string | null>(null);

  // ✅ NEW: Track if user is guest (not logged in) - exposed to components
  const [isGuest, setIsGuest] = useState<boolean>(getUserType() !== "user");

  const fetchCartItems = useCallback(async (forceRefresh = false, clearBeforeFetch = false) => {
    if (clearBeforeFetch) {
      setCartItems([]);
    }
    setLoading(true);
    try {
      const uid = resolveUid();
      const userType = getUserType();
      userTypeRef.current = userType;

      // ✅ For logged-in users, don't read from cookie — use empty variations
      // Backend manages cart for users, cookies are guest/random only
      const cartCookie = userType === "user" ? [] : getCartItems();

      const payload = {
        uid,
        variations: cartCookie.map((i) => ({
          mappingCode: i.mappingCode,
          qty: i.qty,
          isOutOfStock: false,
        })),
        d: getEncodedTimestamp(),
        mode: "fetchCartItems",
      };

      const resp = await fetchBag(payload);

      // API may return either an array in `resp.data` (old shape)
      // or an envelope with `resp.data.items` and `resp.data.enable_mode` (new shape)
      const itemsArray = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.items)
          ? resp.data.items
          : Array.isArray(resp?.items)
            ? resp.items
            : [];

      const modeFromResp = resp?.data?.enable_mode ?? resp?.enable_mode;
      if (modeFromResp && typeof modeFromResp === "object") {
        setEnableMode({
          cash: !!modeFromResp.cash,
          online: !!modeFromResp.online,
        });
      }
      if (Array.isArray(itemsArray)) {
        const items: CartProduct[] = itemsArray.map((p: any) => {
          const mapping =
            String(p.sellerPriceDTO?.mappingCode || p.mappingCode || p.variationCode);

          // ✅ For logged-in users: ONLY use backend response, NO cookie comparison
          if (userType === "user") {
            return {
              ...p,
              quantity: p.qty ?? 1,
            };
          }

          // ✅ For guest/random users: compare backend with cookie (if cookie exists)
          const cookieItem = cartCookie.find(
            (c) => String(c.mappingCode) === mapping
          );

          const backendQty = p.qty ?? 1;
          const cookieQty = cookieItem?.qty ?? 1;

          return {
            ...p,
            quantity: Math.max(backendQty, cookieQty),
          };
        });
        setCartItems(items);
        // ✅ Select all items by default
        setSelectedIds(items.map((p) => String(p.sellerPriceDTO?.mappingCode || p.mappingCode || p.variationCode)));
      }
    } catch {
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Initial fetch on mount - prevent duplicate calls for logged-in users on reload
  useEffect(() => {
    // For logged-in users, only fetch once on mount
    const userType = userTypeRef.current || getUserType();
    userTypeRef.current = userType;

    // ✅ Update isGuest state based on current user type
    setIsGuest(userType !== "user");

    if (userType === "user" && hasFetchedRef.current) {
      setLoading(false);
      return;
    }

    fetchCartItems();
    hasFetchedRef.current = true;

    // Cleanup function to reset flag when component unmounts
    return () => {
      // Only reset if not logged in, so logged-in users keep their guard
      if (getUserType() !== "user") {
        hasFetchedRef.current = false;
      }
    };
  }, []);

  // ✅ Quantity handler with mappingCode consistency
  const handleQuantityChange = async (mappingCode: string, newQty: number) => {
    const userType = getUserType();
    // Helper to match items by mappingCode (consistent with ProductCartCard)
    const matchesMapping = (item: CartProduct) => {
      const itemMapping = String(item.sellerPriceDTO?.mappingCode || item.mappingCode || item.variationCode);
      return itemMapping === String(mappingCode);
    };
    // ✅ Optimistic UI update
    setCartItems((prev) =>
      prev.map((i) =>
        matchesMapping(i) ? { ...i, quantity: newQty } : i
      )
    );

    // ✅ Update cookie ONLY for guest/random users
    // Logged-in users rely on backend/Redux, not cookies
    if (userType !== "user") {
      updateCartItem(mappingCode, newQty);
    }

    setPendingQtyUpdates((p) => ({ ...p, [mappingCode]: newQty }));

    try {
      const payload = {
        uid: resolveUid(),
        variations: [{ mappingCode, qty: newQty, isOutOfStock: false }],
        d: getEncodedTimestamp(),
        mode: "updateQty",
      };

      const resp = await fetchBag(payload);

      const itemsArray = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.items)
          ? resp.data.items
          : Array.isArray(resp?.items)
            ? resp.items
            : [];

      if (resp?.messageCode === 100 && Array.isArray(itemsArray)) {
        // Find updated item using same matching logic
        const updated = itemsArray.find((p: any) => {
          const respMapping = String(p.sellerPriceDTO?.mappingCode || p.mappingCode || p.variationCode);
          return respMapping === String(mappingCode);
        });

        if (updated) {
          const apiQty = updated.qty ?? newQty;

          setCartItems((prev) =>
            prev.map((i) =>
              matchesMapping(i) ? { ...i, quantity: apiQty } : i
            )
          );

          // ✅ Update cookie ONLY for guest/random users
          if (userType !== "user") {
            updateCartItem(mappingCode, apiQty);
          }
        }
      }
    } catch (err) {
      console.error("Qty update failed", err);
    } finally {
      setPendingQtyUpdates((p) => {
        const c = { ...p };
        delete c[mappingCode];
        return c;
      });
    }
  };

  const proceed = async (
    mode: number,
    selectedItems: CartProduct[],
    addressCode?: string | null,
    totalAmount?: number,
  ) => {
    try {
      const payload = {
        uid: resolveUid(),
        variations: selectedItems.map((i) => ({
          mappingCode: i.sellerPriceDTO?.mappingCode,
          qty: i.quantity,
          isOutOfStock: i.isStockOut ?? false,
        })),
        addressCode,
        d: getEncodedTimestamp(),
        mode,
      };

      const resp = await proceedOrder(payload);

      if (resp?.messageCode === 100) {
        // If online payment, open Razorpay popup using order id returned by server
        if (mode === PAYMENT_MODE.ONLINE) {
          const orderId = String(resp?.data?.orderNo || resp?.data?.orderId || resp?.data?.razorpayOrderId || resp?.orderNo || "").trim();
          // const updatedOrderId = `1${orderId}`;
          if (orderId) {
            try {
              // amount should be provided by caller (in rupees). Fallback to 0 if missing.
              const amount = typeof totalAmount === "number" ? totalAmount : 0;

              const rzpResp = await openRazorpayCheckout({
                amount,
                currency: "INR",
                name: "Sardar Times94",
                description: resp?.data?.receiptNo || `Order ${orderId}`,
                // Prefer server-provided key if available (resp.data.key); helper falls back to env
                key: resp?.data?.key || resp?.key || undefined,
                order_id: orderId,
              });


              // On successful payment, collect Razorpay response fields
              const paymentId = rzpResp && (rzpResp as any).razorpay_payment_id;
              const razorpayOrderId = rzpResp && (rzpResp as any).razorpay_order_id;
              const razorpaySignature = rzpResp && (rzpResp as any).razorpay_signature;

              // Send all three Razorpay identifiers to finalizeOrder for backend verification
              // Send Razorpay identifiers both at top-level (legacy) and
              // nested under `razorpay` for clearer payload semantics.
              const final = await finalizeOrder({
                payment_id: paymentId,
                order_id: razorpayOrderId,
                signature: razorpaySignature,
                token: resp.data?.token,
                uid: resolveUid(),
                d: getEncodedTimestamp(),
              });

              if (final?.messageCode === 100) {
                selectedItems.forEach((i) => removeCartItem(i.variationCode));
                return { status: "ok" as const };
              }

              // Finalize failed — attempt to cancel the order server-side
              try {
                await cancelOrder({ token: resp.data?.token, uid: resolveUid(), d: getEncodedTimestamp() });
              } catch (cErr) {
                console.warn("Order cancel failed", cErr);
              }

              return { status: "error" as const };
            } catch (err) {
              // Payment popup closed or failed
              console.error("Razorpay checkout failed", err);
              // If the popup failed, attempt to cancel server order to avoid dangling orders
              try {
                await cancelOrder({ token: resp.data?.token, uid: resolveUid(), d: getEncodedTimestamp() });
              } catch (cErr) {
                console.warn("Order cancel failed after popup failure", cErr);
              }
              return { status: "payment_failed" as const };
            }
          }

          return { status: "payment_failed" as const };
        }

        // Non-online flows: finalize immediately
        try {
          const final = await finalizeOrder({
            trnxNo: "x",
            token: resp.data?.token,
            uid: resolveUid(),
            d: getEncodedTimestamp(),
          });

          if (final?.messageCode === 100) {
            selectedItems.forEach((i) => removeCartItem(i.variationCode));
            return { status: "ok" as const };
          }
        } catch (finalErr) {
          // Finalize failed for non-online flow — attempt cancel
          try {
            await cancelOrder({ token: resp.data?.token, uid: resolveUid(), d: getEncodedTimestamp() });
          } catch (cErr) {
            console.warn("Order cancel failed after finalize error", cErr);
          }
          return { status: "error" as const };
        }
      }
      return { status: "error" as const };
    } catch {
      return { status: "error" as const };
    } finally {
      setLoading(false);
    }
  };

  return {
    cartItems,
    selectedIds,
    setSelectedIds,
    loading,
    pendingQtyUpdates,
    isGuest,  // ✅ NEW: Expose isGuest to components
    enableMode,
    handleQuantityChange,
    proceed,
    fetchCartItems,
  } as const;
};
